<script setup>
// BonBoZ: popup cuộc gọi Zalo. Một hộp thoại cho hai cảnh:
//   - khách đang gọi: chuông LẶP tới khi kết thúc / bấm Bỏ qua / đóng popup
//   - cuộc gọi kết thúc: LUÔN bật lại (kể cả vừa đóng popup lúc reo), tự đóng 8s
// Nghe realtime `bonboz.cuoc_goi` (cắm ở helper/actionCable.js), lọc theo phạm
// vi trang đang xem y như popup mất kết nối. Gọi ĐI không bao giờ popup.
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { useEmitter } from 'dashboard/composables/emitter';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import {
  SU_KIEN_CUOC_GOI,
  TU_DONG_DONG_MS,
  DUONG_CHUONG,
  nenHienPopup,
  chuanHoa,
  tomTat,
  bieuTuong,
  mauBieuTuong,
} from 'dashboard/helper/cuocGoiZalo';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { selectedInboxIds } = useSelectedPages();

const dialogRef = ref(null);
const dangMo = ref(false);
const cuocGoi = ref(null);

const dangReo = computed(() => cuocGoi.value?.trangThai === 'dang_goi');
const khach = computed(() => cuocGoi.value?.khach || {});
const nhan = computed(() => (cuocGoi.value ? tomTat(cuocGoi.value, t) : ''));

// Chuông và hẹn giờ nằm ngoài reactive: không có gì trong template đọc chúng.
let chuong = null;
let henDong = null;

const dungChuong = () => {
  if (!chuong) return;
  chuong.pause();
  chuong.currentTime = 0;
};

const keuChuong = () => {
  if (!chuong) {
    chuong = new Audio(DUONG_CHUONG);
    chuong.loop = true;
  }
  chuong.currentTime = 0;
  // Trình duyệt chặn phát tự động tới khi người dùng đã bấm vào trang: nuốt lỗi
  // chứ đừng để popup chết theo. Mất tiếng thôi, vẫn thấy khách đang gọi.
  chuong.play().catch(() => {});
};

const huyHen = () => {
  if (henDong) clearTimeout(henDong);
  henDong = null;
};

const dong = () => dialogRef.value?.close();

// Dialog phát `close` cho MỌI đường ra (nút, X, Esc, bấm ra ngoài) — một chỗ
// duy nhất phải tắt chuông và huỷ hẹn giờ.
const onDong = () => {
  dangMo.value = false;
  dungChuong();
  huyHen();
};

// showModal() trên hộp thoại đang mở là lỗi, nên tự nhớ trạng thái.
const mo = () => {
  if (dangMo.value) return;
  dialogRef.value?.open();
  dangMo.value = true;
};

const moHoiThoai = () => {
  const { accountId } = route.params;
  const id = cuocGoi.value?.conversationId;
  dong();
  if (!accountId || !id) return;
  router.push({ path: frontendURL(conversationUrl({ accountId, id })) });
};

useEmitter(SU_KIEN_CUOC_GOI, data => {
  // Bấm nghe (trên điện thoại) mà chuông dashboard còn kêu là tiếng ồn.
  if (data?.loai === 'da_nghe') {
    if (dangMo.value && cuocGoi.value?.callId === data.call_id) dong();
    return;
  }
  if (!nenHienPopup(data, selectedInboxIds.value)) return;

  // Cuộc mới đè cuộc cũ: tắt chuông cũ, huỷ hẹn đóng cũ trước khi đổi nội dung.
  dungChuong();
  huyHen();
  cuocGoi.value = chuanHoa(data);
  mo();
  if (dangReo.value) keuChuong();
  else henDong = setTimeout(dong, TU_DONG_DONG_MS);
});

onBeforeUnmount(() => {
  dungChuong();
  huyHen();
});
</script>

<template>
  <Dialog ref="dialogRef" width="sm" @close="onDong">
    <div v-if="cuocGoi" class="flex flex-col items-center gap-3 text-center">
      <Avatar
        :src="khach.avatar || ''"
        :name="khach.ten || '?'"
        :size="64"
        rounded-full
      />
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ khach.ten || t('BONBOZ.CUOC_GOI.TIEU_DE') }}
        </h3>
        <p v-if="khach.sdt" class="mb-0 text-sm text-n-slate-11">
          {{ khach.sdt }}
        </p>
      </div>
      <div class="flex items-center gap-2 text-sm text-n-slate-12">
        <span
          class="flex items-center justify-center rounded-full size-7"
          :class="mauBieuTuong(cuocGoi)"
        >
          <span class="size-4" :class="bieuTuong(cuocGoi)" />
        </span>
        <span>{{ nhan }}</span>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-center gap-3">
        <Button
          type="button"
          variant="faded"
          color="slate"
          :label="
            dangReo ? t('BONBOZ.CUOC_GOI.BO_QUA') : t('BONBOZ.CUOC_GOI.DONG')
          "
          @click="dong"
        />
        <Button
          v-if="cuocGoi?.conversationId"
          type="button"
          color="blue"
          :label="t('BONBOZ.CUOC_GOI.MO_HOI_THOAI')"
          @click="moHoiThoai"
        />
      </div>
    </template>
  </Dialog>
</template>
